import { ApolloError, gql, useApolloClient } from "@apollo/client";
import { Intent } from "@blueprintjs/core";
import {
  AppSessionId,
  CellType,
  GraphQLErrorCode,
  HexId,
  HexVersionId,
  HexVersionString,
  ParameterInputType,
  ParameterOutputType,
  PublishedAppViewInAppReferrer,
  PublishedAppViewInAppReferrerLiteral,
  SESSION_STATE_PREFIX,
  SavedViewId,
  getErrorMessagesWithCode,
  guardNever,
  hexIdToHumanHexId,
} from "@hex/common";
import React, { useState } from "react";

import { ParamParseError } from "../../generated/graphqlTypes";
import { useIsMounted } from "../../hooks/useIsMounted.js";
import { useOnInitialMount } from "../../hooks/useOnInitialMount";
import { useStableRef } from "../../hooks/useStableRef";
import { ORG_ID } from "../../orgs";
import {
  DISABLE_APP_SESSION_REUSE_QUERY_PARAM,
  Routes,
  SAVED_VIEW_QUERY_PARAM,
} from "../../route/routes.js";
import { LoadingView } from "../../views/LoadingView";
import { ErrorPage, ErrorType } from "../common/ErrorPage";
import { useToaster } from "../common/Toasts";

import {
  GetAppSessionForAppSessionLoaderDocument,
  GetAppSessionForAppSessionLoaderQuery,
  GetAppSessionForAppSessionLoaderQueryVariables,
  useAppSessionCreateProjectViewMutation,
  useRunHexWithParamsMutation,
} from "./AppSessionLoader.generated";

gql`
  fragment AppSessionLoaderFragment on AppSession {
    id
    canEdit
    lastRunStart
    snapshot
    parameterized
    savedView {
      id
      name
    }
    hexVersion {
      id
      version
      logicSessionId
      hexId
      canEdit
      published
      prerunApp
      cellsUsedInApp {
        id
        cellType
      }
      hex {
        id
        cacheDefaultAppState
        autoRerun
      }
    }
  }
`;

gql`
  mutation RunHexWithParams(
    $hexId: HexId!
    $version: HexVersionString!
    $savedViewId: SavedViewId
    $preview: Boolean
    $paramValues: JSONObject
    $sessionStates: JSONObject
    $disableAppSessionReuse: Boolean
  ) {
    runHexVersionWithParams(
      hexId: $hexId
      version: $version
      savedViewId: $savedViewId
      preview: $preview
      paramValues: $paramValues
      sessionStates: $sessionStates
      disableAppSessionReuse: $disableAppSessionReuse
    ) {
      appSession {
        ...AppSessionLoaderFragment
        hexVersion {
          id
          defaultAppSessionForViewer(savedViewId: $savedViewId) {
            defaultAppSessionId
          }
        }
      }
      hex {
        id
        title
      }
      isReusingAppSession
      createdFromSavedViewId
      notFoundParams
      invalidParams {
        paramName
        paramValue
        inputCellType
        dataType
        appSessionId
      }
    }
  }
`;

gql`
  query GetAppSessionForAppSessionLoader($appSessionId: AppSessionId!) {
    appSessionById(appSessionId: $appSessionId) {
      ...AppSessionLoaderFragment
    }
  }
`;

gql`
  mutation AppSessionCreateProjectView(
    $hexId: HexId!
    $hexVersionId: HexVersionId!
    $appSessionId: AppSessionId!
    $isSnapshot: Boolean!
    $isLogicView: Boolean!
    $isNotionEmbed: Boolean!
    $appSessionSpecified: Boolean!
    $parameterized: Boolean
    $inAppReferrer: PublishedAppViewInAppReferrer
    $inAppReferrerSource: String
  ) {
    createProjectView(
      hexId: $hexId
      hexVersionId: $hexVersionId
      appSessionId: $appSessionId
      isSnapshot: $isSnapshot
      isLogicView: $isLogicView
      isNotionEmbed: $isNotionEmbed
      appSessionSpecified: $appSessionSpecified
      parameterized: $parameterized
      inAppReferrer: $inAppReferrer
      inAppReferrerSource: $inAppReferrerSource
    ) {
      id
    }
  }
`;

const OUTPUT_TYPE_TO_DISPLAY_TYPE: Record<ParameterOutputType, string> = {
  [ParameterOutputType.STRING]: "string",
  [ParameterOutputType.NUMBER]: "number",
  [ParameterOutputType.BOOLEAN]: "boolean",
  [ParameterOutputType.DATA_FRAME]: "dataframe",
  [ParameterOutputType.DATETIME]: "datetime",
  [ParameterOutputType.DYNAMIC]: "dynamic",
  [ParameterOutputType.LIST_NUMBER]: "list of numbers",
  [ParameterOutputType.LIST_STRING]: "list of strings",
};

const INPUT_TYPE_TO_DISPLAY_TYPE: Record<ParameterInputType, string> = {
  [ParameterInputType.BUTTON]: "button",
  [ParameterInputType.CHECKBOX]: "checkbox",
  [ParameterInputType.DATE]: "date",
  [ParameterInputType.DROPDOWN]: "dropdown",
  [ParameterInputType.MULTISELECT]: "multiselect",
  [ParameterInputType.NUMERIC_INPUT]: "numeric input",
  [ParameterInputType.SLIDER]: "slider",
  [ParameterInputType.TABLE]: "table",
  [ParameterInputType.TEXT_INPUT]: "text input",
  [ParameterInputType.FILE_UPLOAD]: "file upload",
};

function generateInvalidParamToast(
  invalidParams?: readonly ParamParseError[],
  notFoundParams?: readonly string[],
): JSX.Element {
  return (
    <>
      {notFoundParams != null &&
        notFoundParams.length > 0 &&
        (notFoundParams.length === 1 ? (
          <p>
            The input parameter &quot;{notFoundParams[0]}&quot; does not exist
            in this app and was ignored.
          </p>
        ) : (
          <p>
            The input parameters{" "}
            {notFoundParams.map((param) => `"${param}"`).join(", ")} do not
            exist in this app and were ignored.
          </p>
        ))}
      {invalidParams &&
        invalidParams.length > 0 &&
        (invalidParams.length === 1 ? (
          <p>
            The value of input parameter &quot;{invalidParams[0].paramName}
            &quot; (&quot;{invalidParams[0].paramValue}&quot;) is invalid for a{" "}
            {
              INPUT_TYPE_TO_DISPLAY_TYPE[
                invalidParams[0].inputCellType as ParameterInputType
              ]
            }{" "}
            and was ignored (expected a valid{" "}
            {
              OUTPUT_TYPE_TO_DISPLAY_TYPE[
                invalidParams[0].dataType as ParameterOutputType
              ]
            }
            ).
          </p>
        ) : (
          <>
            <p>The following input parameters were invalid and ignored:</p>
            <ul>
              {invalidParams.map((param) => (
                <li key={param.paramName}>
                  expected a valid{" "}
                  {
                    OUTPUT_TYPE_TO_DISPLAY_TYPE[
                      param.dataType as ParameterOutputType
                    ]
                  }{" "}
                  for{" "}
                  {
                    INPUT_TYPE_TO_DISPLAY_TYPE[
                      param.inputCellType as ParameterInputType
                    ]
                  }{" "}
                  parameter &quot;{param.paramName}&quot; but received &quot;
                  {param.paramValue}&quot;
                </li>
              ))}
            </ul>
          </>
        ))}
    </>
  );
}

export type AppSessionLoaderCallback = (args: {
  appSessionId: AppSessionId;
  defaultAppSessionId: AppSessionId | null;
  hexVersion: HexVersionString;
  hexVersionId: HexVersionId;
  savedViewId?: SavedViewId;
  sessionEditable: boolean;
  projectVersionEditable: boolean;
  isLogicSession: boolean;
  isAutoRerunOn: boolean;
  isLikelyUnrunCachedDefaultState: boolean;
  /** Whether an app has elements that are likely to be interacted with or not.
   * This is a heuristic and not an exact value. */
  appLikelyIsInteractive: boolean;
}) => void;

export interface LoadByHexIdAndVersionProps {
  onLoad: AppSessionLoaderCallback;
  onUnauthorized?: undefined;

  hexId: HexId;
  version: HexVersionString;
  savedViewId?: SavedViewId;

  preview?: boolean;
  appSessionId?: undefined;
  trackAppView?: boolean;
  notionEmbedded?: boolean;
}

export interface LoadByAppSessionIdProps {
  onLoad: AppSessionLoaderCallback;
  onUnauthorized: () => void;

  hexId?: undefined;
  version?: undefined;
  savedViewId?: undefined;

  preview?: undefined;
  appSessionId: AppSessionId;
  trackAppView?: boolean;
  notionEmbedded?: boolean;
}

export type AppSessionLoaderProps =
  | LoadByHexIdAndVersionProps
  | LoadByAppSessionIdProps;

export const IN_APP_REFERRER_PARAM = "app_referrer";
export const IN_APP_REFERRER_SOURCE_PARAM = "referrer_source";
/**
 * Creates a new AppSession from a given HexId and Version
 * or loads an existing AppSession from a given AppSessionId,
 * and passes back the loaded session/project info via a callback.
 * The reason we do it in a component instead of a hook is so we can conditionally call this loader depending on if we've been given an already loaded AppSession
 * Or if we need to load a fresh one. This makes it easer to re-use the same component for things like AppRoute and ProjectRoutes that can optionally take an AppSessionId as part of the URL.
 */
export const AppSessionLoader = (
  props: AppSessionLoaderProps,
): JSX.Element | null => {
  const [createProjectView] = useAppSessionCreateProjectViewMutation();
  const { appSessionId, onLoad, onUnauthorized, savedViewId } = props;
  const appSessionIdSpecified = appSessionId != null;

  const [runHex] = useRunHexWithParamsMutation();
  const client = useApolloClient();
  const toaster = useToaster();

  const [state, setState] = useState<"loading" | "errored" | "done">("loading");
  const [errorType, setErrorType] = useState<ErrorType>("other");
  const isMounted = useIsMounted();

  // Make sure that if `onLoad` changes, we don't rerun our RunHex mutation effect below
  const stableOnLoad = useStableRef(onLoad);
  const stableOnUnauthorized = useStableRef(onUnauthorized);

  useOnInitialMount(() => {
    setState("loading");
    const queryParams = new URLSearchParams(window.location.search);

    const paramValues: Record<string, string> = {};
    const sessionStates: Record<string, string> = {};

    for (const [key, value] of Object.entries(
      Object.fromEntries(queryParams),
    )) {
      if (key.startsWith(SESSION_STATE_PREFIX)) {
        sessionStates[key] = value;
        queryParams.delete(key);
      } else if (key.startsWith("_")) {
        paramValues[key] = value;
        queryParams.delete(key);
      }
    }

    let inAppReferrer: PublishedAppViewInAppReferrer | null = null;
    const rawInAppReferrer = queryParams.get(IN_APP_REFERRER_PARAM);

    const inAppReferrerSource = queryParams.get(IN_APP_REFERRER_SOURCE_PARAM);
    if (inAppReferrerSource) {
      queryParams.delete(IN_APP_REFERRER_SOURCE_PARAM);
    }

    if (rawInAppReferrer != null) {
      inAppReferrer = PublishedAppViewInAppReferrerLiteral.guard(
        rawInAppReferrer,
      )
        ? rawInAppReferrer
        : null;
      queryParams.delete(IN_APP_REFERRER_PARAM);
    }

    if (
      Object.keys(paramValues).length > 0 ||
      Object.keys(sessionStates).length > 0 ||
      inAppReferrer != null
    ) {
      const url = new URL(window.location.href);
      url.search = queryParams.toString();
      url.hash = window.location.hash;
      window.history.replaceState(null, "", url.toString());
    }

    let savedViewIdForAppSession: SavedViewId | undefined;
    const appSessionPromise =
      props.hexId != null
        ? runHex({
            variables: {
              hexId: props.hexId,
              version: props.version,
              preview: props.preview ?? null,
              savedViewId: savedViewId ?? null,
              paramValues,
              sessionStates,
              disableAppSessionReuse:
                props.notionEmbedded === true ||
                queryParams.get(DISABLE_APP_SESSION_REUSE_QUERY_PARAM) ===
                  "true",
            },
          }).then(({ data }) => {
            const { invalidParams, notFoundParams } =
              data?.runHexVersionWithParams ?? {};
            // Check if the savedViewId is applied, otherwise show a toast message.
            if (props.savedViewId != null) {
              if (
                data?.runHexVersionWithParams.createdFromSavedViewId != null
              ) {
                savedViewIdForAppSession =
                  data?.runHexVersionWithParams.createdFromSavedViewId;
              } else {
                toaster.show({
                  message: "The specified view was not found",
                  intent: Intent.WARNING,
                });

                // find the view in the queryParam and delete it
                if (queryParams.has(SAVED_VIEW_QUERY_PARAM)) {
                  queryParams.delete(SAVED_VIEW_QUERY_PARAM);
                }
              }
            }

            // url without query params or hash
            let href = window.location.href;

            if (queryParams.has(DISABLE_APP_SESSION_REUSE_QUERY_PARAM)) {
              queryParams.delete(DISABLE_APP_SESSION_REUSE_QUERY_PARAM);
              // NOVA-3997: add app session id to url when we're explicitly starting a new session
              if (data?.runHexVersionWithParams?.appSession?.id != null) {
                href = Routes.APP_LATEST.href(ORG_ID, true, {
                  hexLocator: hexIdToHumanHexId(
                    data.runHexVersionWithParams.hex.id,
                    data.runHexVersionWithParams.hex.title,
                  ),
                  appSessionId: data.runHexVersionWithParams.appSession.id,
                });
              }
            }

            const url = new URL(href);
            url.search = queryParams.toString();
            url.hash = window.location.hash;
            window.history.replaceState(null, "", url.toString());

            if (
              (invalidParams?.length ?? 0) + (notFoundParams?.length ?? 0) >
              0
            ) {
              toaster.show({
                message: generateInvalidParamToast(
                  invalidParams,
                  notFoundParams,
                ),
                intent: Intent.WARNING,
              });
            }
            return {
              appSession: data?.runHexVersionWithParams?.appSession,
            };
          })
        : client
            .query<
              GetAppSessionForAppSessionLoaderQuery,
              GetAppSessionForAppSessionLoaderQueryVariables
            >({
              query: GetAppSessionForAppSessionLoaderDocument,
              variables: { appSessionId: props.appSessionId },
            })
            .then(({ data }) => ({
              appSession: data.appSessionById,
            }));

    appSessionPromise
      .then(async ({ appSession }) => {
        if (appSession) {
          const projectVersionEditable = appSession.hexVersion.canEdit;
          const sessionEditable = appSession.canEdit;
          const isLogicSession =
            appSession.id === appSession.hexVersion.logicSessionId;

          const defaultAppSessionId =
            "defaultAppSessionForViewer" in appSession.hexVersion
              ? appSession.hexVersion.defaultAppSessionForViewer
                  .defaultAppSessionId
              : null;

          stableOnLoad.current({
            appSessionId: appSession.id,
            defaultAppSessionId,
            hexVersion: appSession.hexVersion.version,
            hexVersionId: appSession.hexVersion.id,
            isAutoRerunOn: appSession.hexVersion.hex.autoRerun,
            savedViewId: savedViewIdForAppSession,
            projectVersionEditable,
            sessionEditable,
            isLogicSession,
            // An app session is probably an untouched clone of cached default state
            // if the below criteria are true. I'm not 100% sure this logic is foolproof,
            // but we use it downstream to determine whether to prerun an app session or not,
            // so if it's wrong in a case or two, not a huge deal.
            // note: prerun deprecated 6/24/25
            isLikelyUnrunCachedDefaultState:
              appSession.hexVersion.hex.cacheDefaultAppState &&
              !appSession.parameterized &&
              !appSession.snapshot &&
              !isLogicSession &&
              appSession.lastRunStart == null,
            appLikelyIsInteractive: appSession.hexVersion.cellsUsedInApp.some(
              (c) => {
                switch (c.cellType) {
                  // Cells, that when in an app, are definitely interactive.
                  case CellType.FILTER:
                  case CellType.INPUT:
                  case CellType.DISPLAY_TABLE:
                  case CellType.PIVOT:
                  case CellType.SQL:
                  case CellType.CHART:
                  case CellType.EXPLORE:
                    return true;
                  // Cell that don't have any app interaction which trigger kernel code
                  case CellType.CELL_GROUP:
                  case CellType.CODE:
                  case CellType.DBT_METRIC:
                  case CellType.MAP:
                  case CellType.MARKDOWN:
                  case CellType.METRIC:
                  case CellType.TEXT:
                  case CellType.VEGA_CHART:
                  case CellType.WRITEBACK:
                  case CellType.COMPONENT_IMPORT:
                  case CellType.COLLAPSIBLE:
                  case CellType.BLOCK:
                    return false;
                  default:
                    guardNever(
                      c.cellType,
                      (c.cellType as { cellType: string }).cellType,
                    );
                    return false;
                }
              },
            ),
          });

          if (appSession.hexVersion.published) {
            await createProjectView({
              variables: {
                hexId: appSession.hexVersion.hexId,
                hexVersionId: appSession.hexVersion.id,
                appSessionId: appSession.id,
                parameterized: appSession.parameterized,
                appSessionSpecified: appSessionIdSpecified,
                isLogicView: false,
                isNotionEmbed: props.notionEmbedded ?? false,
                isSnapshot: appSession.snapshot,
                inAppReferrer: inAppReferrer,
                inAppReferrerSource: inAppReferrerSource,
              },
            });
          }

          if (isMounted.current) {
            setState("done");
          }
        }
      })
      .catch((e) => {
        if (props.hexId != null) {
          console.error("Error running hex", e);
        } else {
          let unhandledError = true;
          if (e instanceof ApolloError) {
            const unauthorizedError = getErrorMessagesWithCode(
              e,
              GraphQLErrorCode.FORBIDDEN,
            );
            if (unauthorizedError.length > 0) {
              if (stableOnUnauthorized.current) {
                stableOnUnauthorized.current();
              }
              setErrorType("session-not-viewable");
              unhandledError = false;
            }
          }
          if (unhandledError) {
            console.error("Error fetching app session", e);
          }
        }

        if (isMounted.current) {
          setState("errored");
        }
      });
  });

  switch (state) {
    case "loading":
      return <LoadingView />;
    case "errored":
      return <ErrorPage errorType={errorType} resourceType="app" />;
    case "done":
    default:
      return null;
  }
};
